<template>
    <tailwind-layout title="Self Reporting">
        <template #header>
            <!-- 86bc1ymu6: the old blue banner is now a sub-header, and the CTA
                 reads "Start New Report" (86bcahgev). It only sits in the header when the
                 user already has reports; with none, the empty state below
                 carries it instead. -->
            <div class="px-4 sm:px-6 lg:px-8 mx-auto mt-1">
                <div class="flex justify-between items-center gap-4">
                    <h1 class="font-semibold text-2xl leading-tight">Self Reporting</h1>
                    <button v-if="hasReports" type="button" class="primaryButton shrink-0" @click="showPurposeModal = true">
                        Start New Report
                    </button>
                </div>
                <p class="mt-1 text-sm text-gray-600">
                    To check your credit score, collection history, eviction history or a complete background check
                    <em>without applying for a property</em>, click &ldquo;Start New Report.&rdquo; Once started,
                    we&rsquo;ll ask you a few basic questions and then pull your custom report.
                </p>
            </div>
        </template>

        <!-- Nothing yet: a single centred call to action, and nothing else.
             The guidance that used to sit here is in the sub-header above.
             86bc70q8c: the call to action now asks why first. -->
        <div v-if="!hasReports" class="flex flex-col items-center justify-center py-20 px-6">
            <self-credit-check-purpose-form :options="purpose_options" />
        </div>

        <div v-else class="overflow-hidden">
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Type
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Report Date
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Expiry Date
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Paid Amount
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200">
                                        <tr v-for="(report, index) in reports"
                                            :class="index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer'"
                                            @click="openReport(report.id)">
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                Background Check
                                            </td>
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ report.formatted_report_date }}
                                            </td>
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ report.expiry_date }}
                                            </td>
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <span v-if="report.is_free">Free</span>
                                                <span v-else-if="report.paid && report.invoice">${{ report.invoice.charged_amount }}</span>
                                            </td>
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <div class="flex items-center gap-3">
                                                    <inertia-link :href="route('reports.dashboard', report.id)"
                                                        class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                        View Report
                                                    </inertia-link>
                                                    <!-- 86bc8uyd2: renters buying a home get the free loan estimate.
                                                         The span stops the click reaching the row, which opens the
                                                         dashboard; Link replaces a click listener put on it directly. -->
                                                    <span v-if="offersLoanEstimate(report)" @click.stop>
                                                        <inertia-link
                                                            :href="route('reports.mortgage-estimate', report.id)"
                                                            class="rounded bg-blue-900 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-blue-800"
                                                            data-test="estimate-my-loan">
                                                            Estimate My Loan
                                                        </inertia-link>
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr v-show="!reports.length">
                                            <td colspan="5"
                                                class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                                No Reports were found.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list"
                                    class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(report, index) in reports" :key="index"
                                        class="bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border">
                                        <inertia-link :href="route('reports.dashboard', report.id)">
                                            <div class="flex flex-col p-6">
                                                <h3 class="truncate text-sm font-medium text-gray-900">
                                                    Background Check
                                                    Report
                                                </h3>
                                                <h4 class="mt-1 truncate text-sm text-gray-500"><span
                                                        class="text-gray-900">Report Date:</span>
                                                    {{ report.formatted_report_date }}
                                                </h4>
                                                <h4 class="mt-1 truncate text-sm text-gray-500"><span
                                                        class="text-gray-900">Expiry Date:</span>
                                                    {{ report.expiry_date }}
                                                </h4>
                                                <h4 class="mt-1 truncate text-sm text-gray-500"><span
                                                        class="text-gray-900">Paid
                                                        Amount:</span> <span v-if="report.is_free">Free</span>
                                                    <span v-else-if="report.paid && report.invoice">${{
                                                        report.invoice.charged_amount }}</span>
                                                </h4>
                                            </div>
                                        </inertia-link>
                                        <div>
                                            <div class="divide-gray-200 -mt-px flex divide-x">
                                                <div class="flex w-0 flex-1">
                                                    <inertia-link :href="route('reports.dashboard', report.id)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-eyes" /> View Report
                                                    </inertia-link>
                                                </div>
                                                <div v-if="offersLoanEstimate(report)" class="flex w-0 flex-1">
                                                    <inertia-link :href="route('reports.mortgage-estimate', report.id)"
                                                        class="hover:bg-blue-200 relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
                                                        data-test="estimate-my-loan-mobile">
                                                        Estimate My Loan
                                                    </inertia-link>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <TransitionRoot as="template" :show="showPurposeModal">
            <Dialog as="div" class="relative z-50" @close="showPurposeModal = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>
                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative flex w-full justify-center transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 shadow-xl transition-all sm:my-8 sm:max-w-lg sm:p-6">
                                <self-credit-check-purpose-form :options="purpose_options" cancellable @cancel="showPurposeModal = false" />
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import SelfCreditCheckPurposeForm from '@/Components/SelfCreditCheckPurposeForm.vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { router } from '@inertiajs/vue3';

export default {
    components: {
        TailwindLayout,
        SelfCreditCheckPurposeForm,
        Dialog,
        DialogPanel,
        TransitionChild,
        TransitionRoot,
        FontAwesome: FontAwesomeIcon,
    },
    props: {
        reports: Object,
        purpose_options: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            showPurposeModal: false,
        };
    },
    computed: {
        hasReports() {
            return !!(this.reports && this.reports.length);
        },
    },
    methods: {
        openReport(report_id) {
            router.visit(route('reports.dashboard', report_id));
        },
        // 86bc8uyd2: mirrors Report::offersMortgageEstimate(), once the report exists.
        offersLoanEstimate(report) {
            return report.purpose === 'buying_a_home' && !!report.report_id;
        },
    }
}
</script>
